import { useMutation } from "@apollo/client";
import moment from "moment-timezone";

import {
  ATTACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER,
  ATTACH_MULTIPLE_PROCEDURE_TEMPLATES_TO_WORK_ORDER,
  ATTACH_MULTIPLE_FILLED_PROCEDURES_TO_WORK_ORDER,
  DELETE_OWN_OEM_PROCEDURE_TEMPLATE,
  DETACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER,
  DUPLICATE_OWN_OEM_PROCEDURE_TEMPLATE,
  FINALIZE_OWN_OEM_PROCEDURE_INSTANCE,
  GET_OWN_OEM_PROCEDURE_INSTANCE,
  GET_OWN_OEM_TICKET_BY_ID,
  LIST_OEM_PROCEDURE_TEMPLATES_QUERY,
  ProcedureTemplate,
  RESTORE_OWN_OEM_PROCEDURE_FROM_ACTIVITY_LOG,
  SAVE_OWN_OEM_PROCEDURE_INSTANCE,
  SAVE_OWN_OEM_PROCEDURE_TEMPLATE,
  ATTACH_PROCEDURE_TO_PM,
  ProcedureInstance,
  ATTACH_PROCEDURE_TO_TEMPLATE_MUTATION,
  DETACH_PROCEDURE_FROM_TEMPLATE_MUTATION,
  AssetTemplate,
  CREATE_DETACHED_PROCEDURE,
  DELETE_DETACHED_PROCEDURE,
  ATTACH_DETACHED_PROCEDURE_TO_WORK_ORDER,
  UPDATE_PROCEDURE_HEADER_FIELD,
  GET_OWN_OEM_PROCEDURE_TEMPLATE,
  ADD_PROCEDURE_HEADER_CUSTOM_FIELDS,
  REMOVE_PROCEDURE_HEADER_CUSTOM_FIELD,
  UPDATE_DEFAULT_PROCEDURE_HEADER_MUTATION,
  UPDATE_DEFAULT_PROCEDURE_FOOTER_MUTATION,
  REMOVE_DEFAULT_PROCEDURE_HEADER_MUTATION,
  REMOVE_DEFAULT_PROCEDURE_FOOTER_MUTATION,
  UPDATE_PROCEDURE_TEMPLATE_HEADER_MUTATION,
  UPDATE_PROCEDURE_TEMPLATE_FOOTER_MUTATION,
  REMOVE_PROCEDURE_TEMPLATE_HEADER_MUTATION,
  REMOVE_PROCEDURE_TEMPLATE_FOOTER_MUTATION,
  UPDATE_PROCEDURE_PREFERENCES_MUTATION,
  GET_OWN_OEM_QUERY,
  BULK_DELETE_OWN_OEM_PROCEDURE_TEMPLATES_MUTATION,
} from "../../api";
import client from "../../apollo/_client";

import { LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY } from "~/api/_queries";

export const useCreateProcedure = () => {
  const [saveData, { loading, error }] = useMutation(
    SAVE_OWN_OEM_PROCEDURE_TEMPLATE,
  );
  return {
    saveProcedureTemplate: async (input) =>
      saveData({
        variables: { input },
        update(cache, { data: { saveOwnOemProcedureTemplate: ref } }) {
          cache.modify({
            fields: {
              listOwnOemProcedureTemplatesWithCount(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment:
                    ProcedureTemplate.fragments.ProcedureTemplateFullData,
                  fragmentName: "ProcedureTemplateFullData",
                });

                return {
                  totalCount: existingRefs?.totalCount + 1,
                  procedures: [...(existingRefs?.procedures || []), newRef],
                };
              },
            },
          });
        },
      }),
    savingProcedureTemplate: loading,
    errorSavingProcedureTemplate: error,
  };
};

export const useDeleteProcedureTemplate = () => {
  const [deleteOwnOemProcedureTemplate, { loading, error }] = useMutation(
    DELETE_OWN_OEM_PROCEDURE_TEMPLATE,
  );
  return {
    loading,
    error,
    removeProcedureTemplate: async (id) =>
      new Promise((resolve, reject) =>
        deleteOwnOemProcedureTemplate({
          variables: { id },
          update(cache) {
            cache.modify({
              fields: {
                listOwnOemProcedureTemplatesWithCount(
                  existingRefs = [],
                  { readField },
                ) {
                  return {
                    procedures: [...(existingRefs?.procedures || [])].filter(
                      (ref) => {
                        return readField("_id", ref) !== id;
                      },
                    ),
                    totalCount: existingRefs?.totalCount - 1,
                  };
                },
              },
            });
            cache.gc();
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          }),
      ),
  };
};

export const useDuplicateProcedureTemplate = () => {
  const [duplicate, { loading, error }] = useMutation(
    DUPLICATE_OWN_OEM_PROCEDURE_TEMPLATE,
  );
  return {
    loading,
    error,
    duplicateProcedureTemplate: async (id) =>
      new Promise((resolve, reject) =>
        duplicate({
          variables: { id },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          }),
      ),
  };
};

export const useSaveProcedure = () => {
  const [saveData, { loading, error }] = useMutation(
    SAVE_OWN_OEM_PROCEDURE_TEMPLATE,
  );
  return {
    saveProcedureTemplate: async (input) =>
      saveData({
        variables: { input },
        update(cache, { data: { saveOwnOemProcedureTemplate: ref } }) {
          cache.modify({
            id: cache.identify({
              __typename: "ProcedureTemplate",
              id: input._id,
            }),
            fields: {
              listOwnOemProcedureTemplatesWithCount(existingRefs = []) {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment:
                    ProcedureTemplate.fragments.ProcedureTemplateFullData,
                  fragmentName: "ProcedureTemplateFullData",
                });

                return [...existingRefs, newRef];
              },
            },
          });
        },
      }),
    savingProcedureTemplate: loading,
    errorSavingProcedureTemplate: error,
  };
};

export const useAttachProcedure = () => {
  const [attach, { loading, error }] = useMutation(
    ATTACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER,
  );
  return {
    attachProcedure: async (workOrderId, templateId) =>
      attach({
        variables: {
          input: { workOrderId, templateId },
        },
        skip: !workOrderId || !templateId,
      }),
    attachingProcedure: loading,
    errorAttachProcedure: error,
  };
};

export const useAttachMultipleProcedureTemplates = () => {
  const [attach, { loading, error }] = useMutation(
    ATTACH_MULTIPLE_PROCEDURE_TEMPLATES_TO_WORK_ORDER,
  );
  return {
    attachMultipleProcedureTemplates: async (workOrderId, templateIds) =>
      attach({
        variables: {
          input: { workOrderId, templateIds },
        },
        skip: !workOrderId || !templateIds?.length,
        refetchQueries: [
          {
            query: GET_OWN_OEM_TICKET_BY_ID,
            variables: { id: workOrderId },
          },
        ],
        awaitRefetchQueries: true,
      }),
    attachingProcedures: loading,
    errorAttachProcedures: error,
  };
};

export const useAttachMultipleFilledProcedures = () => {
  const [attach, { loading, error }] = useMutation(
    ATTACH_MULTIPLE_FILLED_PROCEDURES_TO_WORK_ORDER,
  );
  return {
    attachMultipleFilledProcedures: async (workOrderId, procedureIds) =>
      attach({
        variables: {
          input: { workOrderId, procedureIds },
        },
        skip: !workOrderId || !procedureIds?.length,
        refetchQueries: [
          {
            query: LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY,
            variables: { params: {} },
          },
          {
            query: GET_OWN_OEM_TICKET_BY_ID,
            variables: { id: workOrderId },
          },
        ],
        awaitRefetchQueries: true,
      }),
    attachingProcedures: loading,
    errorAttachProcedures: error,
  };
};

export const useAttachProcedureToPM = () => {
  const [attach, { loading, error }] = useMutation(ATTACH_PROCEDURE_TO_PM);

  return {
    attachProcedureToPM: async (preventiveMaintenanceId, templateId) => {
      if (!preventiveMaintenanceId || !templateId) {
        throw new Error("preventiveMaintenanceId and templateId are required");
      }

      return attach({
        variables: {
          input: { preventiveMaintenanceId, templateId },
        },
      });
    },
    attachingProcedure: loading,
    errorAttachProcedure: error,
  };
};

export const useDetachProcedure = () => {
  const [detach, { loading, error }] = useMutation(
    DETACH_OWN_OEM_PROCEDURE_TO_WORK_ORDER,
  );

  return {
    detachingProcedure: loading,
    detachProcedureError: error,
    detachProcedure: async (workOrderId, procedureId) =>
      detach({
        variables: {
          input: { workOrderId, procedureId },
        },
        update(cache) {
          try {
            const { getOwnOemTicketById } = cache.readQuery({
              query: GET_OWN_OEM_TICKET_BY_ID,
              variables: {
                id: workOrderId,
              },
            });

            if (!getOwnOemTicketById) return;

            const cloned = { ...getOwnOemTicketById };
            cloned.procedures = cloned.procedures.filter(
              (item) => item.procedure._id !== procedureId,
            );

            cache.writeQuery({
              query: GET_OWN_OEM_TICKET_BY_ID,
              variables: {
                id: workOrderId,
              },
              data: {
                getOwnOemTicketById: cloned,
              },
            });

            cache.gc();
          } catch (error) {
            //
          }
        },
      }),
  };
};

export const useCreateDetachedProcedure = () => {
  const [mutate, { loading, error }] = useMutation(CREATE_DETACHED_PROCEDURE);

  return {
    creatingDetached: loading,
    errorCreatingDetached: error,
    createDetached: async (templateId) =>
      mutate({
        variables: { input: { templateId } },
        refetchQueries: [
          {
            query: LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY,
            variables: { params: {} },
          },
        ],
        awaitRefetchQueries: true,
      }).then(({ data }) => data?.createDetachedProcedure),
  };
};

export const useDeleteDetachedProcedure = () => {
  const [mutate, { loading, error }] = useMutation(DELETE_DETACHED_PROCEDURE);

  return {
    deletingDetached: loading,
    errorDeletingDetached: error,
    deleteDetached: async (id) =>
      mutate({
        variables: { id },
        update(cache) {
          cache.modify({
            fields: {
              listDetachedProcedures(existingRefs = [], { readField }) {
                return {
                  procedures: [...(existingRefs?.procedures || [])].filter(
                    (ref) => {
                      return readField("_id", ref) !== id;
                    },
                  ),
                  totalCount: existingRefs?.totalCount - 1,
                };
              },
            },
          });
          cache.gc();
        },
      }),
  };
};

export const useUpdateProcedureHeaderField = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_PROCEDURE_HEADER_FIELD,
  );

  return {
    loading: loading,
    error: error,
    update: async (input) =>
      mutate({
        variables: { input: input },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: input.procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useAddProcedureHeaderCustomFields = () => {
  const [mutate, { loading, error }] = useMutation(
    ADD_PROCEDURE_HEADER_CUSTOM_FIELDS,
  );

  return {
    loading: loading,
    error: error,
    addCustomFields: async (input) =>
      mutate({
        variables: { input: input },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: input.procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveProcedureHeaderCustomField = () => {
  const [mutate, { loading, error }] = useMutation(
    REMOVE_PROCEDURE_HEADER_CUSTOM_FIELD,
  );

  return {
    loading: loading,
    error: error,
    removeCustomField: async (input) =>
      mutate({
        variables: { input: input },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: input.procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useAttachDetachedProcedureToWorkOrder = () => {
  const [mutate, { loading, error }] = useMutation(
    ATTACH_DETACHED_PROCEDURE_TO_WORK_ORDER,
  );

  return {
    attaching: loading,
    error,
    attachToWorkOrder: async (workOrderId, procedureId) =>
      mutate({
        variables: { input: { workOrderId, procedureId } },
        refetchQueries: [
          {
            query: LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY,
            variables: { params: {} },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useSaveProcedureInstance = () => {
  const [save, { loading, error }] = useMutation(
    SAVE_OWN_OEM_PROCEDURE_INSTANCE,
  );

  return {
    loading,
    error,
    saveInstance: async (input) =>
      save({
        variables: { input },
        skip: !input?._id,
        update(cache, { data: { saveOwnOemProcedure: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Procedure", id: ref._id }),
            fields: {
              getOwnOemProcedureById() {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment:
                    ProcedureInstance.fragments.ProcedureInstanceFullData,
                  fragmentName: "ProcedureInstanceFullData",
                });

                return newRef;
              },
            },
          });
        },
      }),
  };
};

export const useRestoreProcedureInstance = () => {
  const [restore, { loading }] = useMutation(
    RESTORE_OWN_OEM_PROCEDURE_FROM_ACTIVITY_LOG,
  );

  return {
    loading,
    /** mode: "FULL" | "CURRENT_LAYOUT" */
    restoreInstance: async ({ procedureId, activityLogId, mode }) => {
      const { data } = await restore({
        variables: { input: { procedureId, activityLogId, mode } },
        // The Procedure entity is normalized, so the returned fields update
        // the cached procedure; refetch the log so the restore shows up.
        refetchQueries: ["listOwnOemActivityLogs"],
      });
      return data?.restoreOwnOemProcedureFromActivityLog;
    },
  };
};

export const useFinalizeProcedureInstance = () => {
  const [save, { loading, error }] = useMutation(
    FINALIZE_OWN_OEM_PROCEDURE_INSTANCE,
  );

  return {
    loading,
    error,
    finalize: async (input) =>
      save({
        variables: {
          input: { ...input, timezone: moment.tz.guess(true) },
        },
        update(cache, { data: { finalizeOwnOemProcedure: ref } }) {
          cache.modify({
            id: cache.identify({ __typename: "Procedure", id: input._id }),
            fields: {
              getOwnOemProcedureById() {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment:
                    ProcedureInstance.fragments.ProcedureInstanceFullData,
                  fragmentName: "ProcedureInstanceFullData",
                });

                return newRef;
              },
            },
          });
        },
      }),
  };
};

export const updateProcedureInstanceCacheOnFinalized = async (payload) => {
  client.refetchQueries({
    include: [GET_OWN_OEM_PROCEDURE_INSTANCE],
    updateCache(cache) {
      cache.evict({ fieldName: "listOwnOemMachineAgreggatedHistoryById" });
    },
  });
};

export const updateProcedureTemplateCacheOnDuplicate = async (payload) => {
  const procedureTemplate = client.readQuery({
    query: LIST_OEM_PROCEDURE_TEMPLATES_QUERY,
    variables: {
      params: {},
    },
  });
  if (!procedureTemplate) return;

  const newItem = {
    __typename: "ProcedureTemplate",
    _id: payload?._id,
    name: payload?.name,
    createdAt: payload?.createdAt,
    updatedAt: payload?.updatedAt,
    createdBy: {
      __typename: "User",
      _id: payload?.createdBy?._id,
      name: payload?.createdBy?.name,
    },
  };

  const { listOwnOemProcedureTemplatesWithCount } = procedureTemplate;

  const newData = {
    procedures: [
      ...(listOwnOemProcedureTemplatesWithCount?.procedures || []),
      newItem,
    ],
    totalCount: listOwnOemProcedureTemplatesWithCount?.totalCount + 1,
  };

  client.writeQuery({
    query: LIST_OEM_PROCEDURE_TEMPLATES_QUERY,
    variables: {
      params: {},
    },
    data: {
      listOwnOemProcedureTemplatesWithCount: newData,
    },
  });
};

export const useAttachProceduresToAssetTemplate = () => {
  const [attach, { loading, error }] = useMutation(
    ATTACH_PROCEDURE_TO_TEMPLATE_MUTATION,
  );

  return {
    attachProceduresToAssetTemplate: async (procedures, templateId) => {
      if (!procedures || !templateId) {
        throw new Error("procedures and templateId are required");
      }

      return attach({
        variables: {
          procedures,
          templateId,
        },
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemAssetById",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemProcedureTemplatesWithCount",
          });
          cache.gc();
        },
      });
    },
    attachingProcedure: loading,
    errorAttachProcedure: error,
  };
};

export const useDetachProcedureFromAssetTemplate = () => {
  const [detach, { loading, error }] = useMutation(
    DETACH_PROCEDURE_FROM_TEMPLATE_MUTATION,
  );

  return {
    detachProcedureFromAssetTemplate: async (procedureId, templateId) => {
      if (!procedureId || !templateId) {
        throw new Error("procedureId and templateId are required");
      }

      return detach({
        variables: {
          procedureId,
          templateId,
        },
        update(cache) {
          const cacheId = cache.identify({
            __typename: "AssetTemplate",
            id: templateId,
          });
          const documentRef = cache.readFragment({
            id: cacheId,
            fragment: AssetTemplate.fragments.AssetTemplateFullData,
            fragmentName: "AssetTemplateFullData",
          });

          if (!documentRef) return;

          cache.writeFragment({
            id: cacheId,
            fragment: AssetTemplate.fragments.AssetTemplateFullData,
            fragmentName: "AssetTemplateFullData",
            data: {
              ...documentRef,
              procedures: documentRef.procedures.filter(
                (procedure) => procedure.procedureId !== procedureId,
              ),
            },
          });

          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getOwnOemAssetById",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemProcedureTemplatesWithCount",
          });
          cache.gc();
        },
      });
    },
    detachingProcedure: loading,
    errorDetachProcedure: error,
  };
};

export const useUpdateDefaultProcedureHeader = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_DEFAULT_PROCEDURE_HEADER_MUTATION,
  );

  return {
    loading,
    error,
    updateHeader: async (header) =>
      mutate({
        variables: { header },
        refetchQueries: [{ query: GET_OWN_OEM_QUERY }],
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateDefaultProcedureFooter = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_DEFAULT_PROCEDURE_FOOTER_MUTATION,
  );

  return {
    loading,
    error,
    updateFooter: async (footer) =>
      mutate({
        variables: { footer },
        refetchQueries: [{ query: GET_OWN_OEM_QUERY }],
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveDefaultProcedureHeader = () => {
  const [mutate, { loading, error }] = useMutation(
    REMOVE_DEFAULT_PROCEDURE_HEADER_MUTATION,
  );

  return {
    loading,
    error,
    removeHeader: async () =>
      mutate({
        refetchQueries: [{ query: GET_OWN_OEM_QUERY }],
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveDefaultProcedureFooter = () => {
  const [mutate, { loading, error }] = useMutation(
    REMOVE_DEFAULT_PROCEDURE_FOOTER_MUTATION,
  );

  return {
    loading,
    error,
    removeFooter: async () =>
      mutate({
        refetchQueries: [{ query: GET_OWN_OEM_QUERY }],
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateProcedurePreferences = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_PROCEDURE_PREFERENCES_MUTATION,
  );

  return {
    loading,
    error,
    updateProcedurePreferences: async (input) =>
      mutate({
        variables: { input },
        refetchQueries: [{ query: GET_OWN_OEM_QUERY }],
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateProcedureTemplateHeader = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_PROCEDURE_TEMPLATE_HEADER_MUTATION,
  );

  return {
    loading,
    error,
    updateHeader: async ({ procedureId, header }) =>
      mutate({
        variables: { procedureId, header },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useUpdateProcedureTemplateFooter = () => {
  const [mutate, { loading, error }] = useMutation(
    UPDATE_PROCEDURE_TEMPLATE_FOOTER_MUTATION,
  );

  return {
    loading,
    error,
    updateFooter: async ({ procedureId, footer }) =>
      mutate({
        variables: { procedureId, footer },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveProcedureTemplateHeader = () => {
  const [mutate, { loading, error }] = useMutation(
    REMOVE_PROCEDURE_TEMPLATE_HEADER_MUTATION,
  );

  return {
    loading,
    error,
    removeHeader: async (procedureId) =>
      mutate({
        variables: { procedureId },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useRemoveProcedureTemplateFooter = () => {
  const [mutate, { loading, error }] = useMutation(
    REMOVE_PROCEDURE_TEMPLATE_FOOTER_MUTATION,
  );

  return {
    loading,
    error,
    removeFooter: async (procedureId) =>
      mutate({
        variables: { procedureId },
        refetchQueries: [
          {
            query: GET_OWN_OEM_PROCEDURE_TEMPLATE,
            variables: { id: procedureId },
          },
        ],
        awaitRefetchQueries: true,
      }),
  };
};

export const useBulkDeleteProcedureTemplates = () => {
  const [bulkDelete] = useMutation(
    BULK_DELETE_OWN_OEM_PROCEDURE_TEMPLATES_MUTATION,
    {
      update(cache) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: "listOwnOemProcedureTemplatesWithCount",
        });
        cache.gc();
      },
    },
  );

  const bulkDeleteProcedureTemplates = async (ids) => {
    const { data } = await bulkDelete({ variables: { ids } });
    return data.bulkDeleteOwnOemProcedureTemplates;
  };

  return { bulkDeleteProcedureTemplates };
};
